import {
  addDoc,
  deleteDoc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import type { Item, ItemDraft, ItemImage } from '@easymoving/types';
import { itemDoc, itemsCol } from './paths';
import { hydratePrivateImages, imagesForStorage } from './privateImages';
import { toMillis } from './serialize';

function itemFromDoc(snap: QueryDocumentSnapshot<DocumentData> | DocumentData, id: string): Item {
  const d = (typeof (snap as QueryDocumentSnapshot).data === 'function'
    ? (snap as QueryDocumentSnapshot).data()
    : snap) as DocumentData;
  return {
    id,
    name: d.name ?? '',
    nickname: typeof d.nickname === 'string' ? d.nickname : undefined,
    images: imagesForStorage((d.images ?? []) as ItemImage[]),
    room: d.room,
    category: d.category,
    iconType: d.iconType,
    destination: d.destination ?? 'undecided',
    progressStatus: d.progressStatus ?? 'not_started',
    length: d.length,
    width: d.width,
    height: d.height,
    dimensionUnit: d.dimensionUnit,
    dimensionsText: d.dimensionsText,
    weight: d.weight,
    condition: d.condition,
    quantity: d.quantity ?? 1,
    estimatedValue: d.estimatedValue,
    currency: d.currency,
    boxId: d.boxId,
    storageLocation: d.storageLocation,
    fragile: d.fragile ?? false,
    priority: d.priority,
    notes: d.notes,
    privateNotes: d.privateNotes,
    publicNotes: d.publicNotes,
    includeInMoverQuote: d.includeInMoverQuote,
    createdAt: toMillis(d.createdAt),
    updatedAt: toMillis(d.updatedAt),
  };
}

/** Create an item. Returns the new item id. */
export async function createItem(uid: string, draft: ItemDraft): Promise<string> {
  const ref = await addDoc(itemsCol(uid), {
    ...draft,
    destination: draft.destination ?? 'pack',
    progressStatus: draft.progressStatus ?? 'not_started',
    quantity: draft.quantity ?? 1,
    fragile: draft.fragile ?? false,
    images: [],
    // Client timestamp (not serverTimestamp): the items query orders by
    // createdAt, and an unresolved server timestamp is excluded from the
    // ordered result until it acks — so a just-created item would briefly
    // vanish from the list. A concrete client value appears immediately.
    createdAt: Timestamp.now(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function getItem(uid: string, itemId: string): Promise<Item | null> {
  const snap = await getDoc(itemDoc(uid, itemId));
  return snap.exists() ? hydratePrivateImages(itemFromDoc(snap, snap.id)) : null;
}

/** Patch an item. `updatedAt` is bumped automatically. */
export async function updateItem(
  uid: string,
  itemId: string,
  patch: Partial<Omit<Item, 'id' | 'createdAt'>>,
): Promise<void> {
  await updateDoc(itemDoc(uid, itemId), { ...patch, updatedAt: serverTimestamp() });
}

export async function deleteItem(uid: string, itemId: string): Promise<void> {
  await deleteDoc(itemDoc(uid, itemId));
}

/** Duplicate an item (new id, same fields, fresh timestamps, no box). */
export async function duplicateItem(uid: string, itemId: string): Promise<string | null> {
  const src = await getItem(uid, itemId);
  if (!src) return null;
  const { id: _id, createdAt: _c, updatedAt: _u, ...rest } = src;
  const ref = await addDoc(itemsCol(uid), {
    ...rest,
    images: imagesForStorage(rest.images),
    primaryImageUrl: undefined,
    primaryThumbUrl: undefined,
    createdAt: Timestamp.now(), // client value so the copy lists immediately (see createItem)
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

/**
 * Subscribe to all of a user's items, newest first. V1 fetches the full set
 * and filters/sorts/searches client-side (small per-user datasets) — see
 * lib/query/itemFilters.ts. Returns an unsubscribe fn.
 */
export function subscribeItems(
  uid: string,
  cb: (items: Item[]) => void,
  onError?: (e: Error) => void,
): () => void {
  const q = query(itemsCol(uid), orderBy('createdAt', 'desc'));
  let generation = 0;
  return onSnapshot(
    q,
    (snap) => {
      const current = ++generation;
      const stored = snap.docs.map((d) => itemFromDoc(d, d.id));
      // Render metadata/icon fallbacks immediately, then progressively replace
      // them with owner-authenticated blob URLs when each download completes.
      cb(stored);
      void Promise.all(stored.map(hydratePrivateImages)).then((hydrated) => {
        if (current === generation) cb(hydrated);
      });
    },
    (err) => onError?.(err),
  );
}
